import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Link,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { saveDefaultUpiId, sendUpiUpdateOtp } from '../../services/adminSettingsService';
import { UPI_CONSENT_TEXT_EN, UPI_CONSENT_TEXT_TE } from '../../utils/upiPayeeConsent';
import Msg91OtpWidget from '../Msg91OtpWidget';

const upiRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/;

/**
 * Prompt to set billing UPI when QR / payment flows need it.
 * Loads current editable value from GET /api/settings/admin/upi.
 * OTP is sent via MSG91 widget (same as Mitra registration).
 */
export default function UpiSetupDialog({ open, onClose, onSaved }) {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpPhoneMasked, setOtpPhoneMasked] = useState('');
  const [otpPhone, setOtpPhone] = useState('');
  const [msg91AccessToken, setMsg91AccessToken] = useState('');
  const [msg91RequestUserId, setMsg91RequestUserId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [consent, setConsent] = useState(true);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get('/api/settings/admin/upi');
        if (cancelled) return;
        const raw = data?.editable_upi_id ?? data?.upi_id ?? '';
        setValue(typeof raw === 'string' ? raw : '');
        setAccountName(String(data?.upi_account_name || '').trim());
        setConsent(true);
        setOtpPhoneMasked(data?.otp_phone_masked || '');
        setOtpSent(false);
        setOtpPhone('');
        setMsg91AccessToken('');
        setMsg91RequestUserId('');
      } catch {
        if (!cancelled) setValue('');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const sendOtp = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error('Enter a UPI ID (format: name@bank)');
      return false;
    }
    if (!upiRegex.test(trimmed)) {
      toast.error('Invalid UPI format. Example: merchantname@ybl or 9876543210@paytm');
      return false;
    }
    setSendingOtp(true);
    setMsg91AccessToken('');
    setMsg91RequestUserId('');
    try {
      const data = await sendUpiUpdateOtp();
      const masked = data?.otp_phone_masked || data?.data?.otp_phone_masked || otpPhoneMasked;
      const phoneRaw = data?.otp_phone || data?.data?.otp_phone || '';
      const phone = String(phoneRaw).replace(/\D/g, '').slice(-10);
      if (phone.length !== 10) {
        throw new Error('Registered mobile missing from OTP prepare response.');
      }
      if (masked) setOtpPhoneMasked(masked);
      setOtpPhone(phone);
      setOtpSent(true);
      toast.success(`Verify OTP via MSG91 for ${masked || phone}`);
      return true;
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to prepare OTP';
      toast.error(msg);
      return false;
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error('Enter a UPI ID (format: name@bank)');
      return;
    }
    if (!upiRegex.test(trimmed)) {
      toast.error('Invalid UPI format. Example: merchantname@ybl or 9876543210@paytm');
      return;
    }
    const name = accountName.trim();
    if (name.length < 2) {
      toast.error('Enter the account holder name shown in GPay / PhonePe.');
      return;
    }
    if (!consent) {
      toast.error('Please confirm you have checked this UPI ID and name.');
      return;
    }
    if (!otpSent) {
      await sendOtp();
      return;
    }
    if (!msg91AccessToken) {
      toast.error('Verify the MSG91 OTP sent to your registered mobile first.');
      return;
    }
    setSaving(true);
    try {
      await saveDefaultUpiId(trimmed, null, {
        upi_account_name: name,
        upi_consent: true,
        msg91_access_token: msg91AccessToken,
        otp_phone: otpPhone,
        ...(msg91RequestUserId
          ? { msg91_request_user_id: msg91RequestUserId }
          : {}),
      });
      toast.success('UPI ID saved');
      onSaved?.(trimmed);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save UPI';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Set billing UPI</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A valid UPI ID is required to generate payment QR codes. Use your business collection ID
          (format: <strong>name@bank</strong>).
        </Typography>
        {loading ? (
          <CircularProgress size={28} />
        ) : (
          <>
            <TextField
              autoFocus
              margin="dense"
              label="UPI ID"
              fullWidth
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setOtpSent(false);
                setOtpPhone('');
                setMsg91AccessToken('');
                setMsg91RequestUserId('');
              }}
              placeholder="e.g. shopname@ybl"
              helperText="OTP will be sent via MSG91 to the Mitra registered mobile before save."
            />
            <TextField
              margin="dense"
              label="Account holder name (as in GPay / PhonePe)"
              fullWidth
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="e.g. Ravi Kumar"
              helperText="Shown on the QR so everyone can confirm who receives the payment."
              inputProps={{ maxLength: 50 }}
            />
            <FormControlLabel
              sx={{ mt: 1, alignItems: 'flex-start' }}
              control={
                <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  {UPI_CONSENT_TEXT_EN} {UPI_CONSENT_TEXT_TE}
                </Typography>
              }
            />
            {otpSent && otpPhone && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  OTP via MSG91 to {otpPhoneMasked || otpPhone}
                </Typography>
                <Msg91OtpWidget
                  phoneDigits10={otpPhone}
                  disabled={saving}
                  verifyButtonLabel="Verify OTP"
                  onVerified={(token, meta) => {
                    setMsg91AccessToken(token);
                    const sessionId =
                      meta && String(meta.requestUserId || meta.reqId || '').trim();
                    setMsg91RequestUserId(sessionId || '');
                    toast.success('Mobile verified. You can save UPI now.');
                  }}
                  onError={(msg) => {
                    setMsg91AccessToken('');
                    setMsg91RequestUserId('');
                    toast.error(msg || 'MSG91 OTP failed');
                  }}
                />
              </Box>
            )}
          </>
        )}
        <Typography variant="body2" sx={{ mt: 2 }}>
          <Link component="button" type="button" onClick={() => navigate('/bills/admin-controls')}>
            Open full Admin Controls
          </Link>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || sendingOtp}>
          Cancel
        </Button>
        {otpSent && (
          <Button onClick={sendOtp} disabled={saving || sendingOtp || loading}>
            Resend prepare
          </Button>
        )}
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || sendingOtp || loading || (otpSent && !msg91AccessToken)}
        >
          {saving ? 'Saving…' : otpSent ? 'Save UPI' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
